import {
    Slider,
    TextField,
    makeStyles,
    FormControl,
    FormLabel,
    FormHelperText,
} from '@material-ui/core';
import { useField, useFormikContext } from 'formik';
import React from 'react';
import { getLocalizedLabel } from '../utils';
import type { FormField } from '../interfaces';
import classNames from 'classnames';
import { getFormFieldStyles } from '../formFieldStyles';

const useStyles = makeStyles((theme) => ({
    error: {
        color: theme.palette.error.main,
    },
}));

export const FormikSlider = ({
    field,
    label,
    defaultPercentage = 0,
    percentageMarks,
    isReadOnly,
    isRequired,
}: {
    field: FormField;
    label?: string;
    isRequired?: boolean;
    isReadOnly?: boolean;
    defaultPercentage: number;
    percentageMarks?: Array<{ value: number; label: string }>;
}) => {
    const localClasses = useStyles();
    const formFieldClasses = getFormFieldStyles();
    const { setFieldValue, values } = useFormikContext<number>();
    const [formikField, formikFieldMeta] = useField<number>(field.id);

    const sliderValue = Number(values[field.id]) || defaultPercentage;

    return (
        <>
            {isReadOnly ? (
                <TextField
                    {...formikField}
                    fullWidth={true}
                    className={classNames({
                        [formFieldClasses.editableField]: !isReadOnly,
                        [formFieldClasses.readOnlyField]: isReadOnly,
                    })}
                    required={isRequired}
                    variant="outlined"
                    disabled={!!isReadOnly}
                    InputLabelProps={{
                        shrink: true,
                    }}
                    label={getLocalizedLabel(
                        `project.formFields.${field.id}`,
                        field.label,
                        field.id,
                    )}
                    helperText={
                        formikFieldMeta.touched ? formikFieldMeta.error : ''
                    }
                    error={
                        formikFieldMeta.touched &&
                        Boolean(formikFieldMeta.error)
                    }
                />
            ) : (
                <FormControl
                    component="fieldset"
                    disabled={isReadOnly}
                    error={
                        formikFieldMeta.touched &&
                        Boolean(formikFieldMeta.error)
                    }
                    fullWidth={true}
                >
                    <FormLabel
                        className={classNames({
                            [formFieldClasses.editableField]: !isReadOnly,
                            [formFieldClasses.readOnlyField]: isReadOnly,
                        })}
                        component="legend"
                        required={isRequired}
                    >
                        {label}
                    </FormLabel>
                    <Slider
                        {...formikField}
                        id={field.id}
                        name={field.id}
                        getAriaLabel={() => 'Always visible'}
                        value={sliderValue}
                        onChange={(event, newValue) => {
                            setFieldValue(field.id, newValue);
                        }}
                        valueLabelDisplay="on"
                        marks={percentageMarks}
                        getAriaValueText={(value) => `${value}%`}
                        defaultValue={defaultPercentage}
                        step={1}
                        disabled={isReadOnly}
                    />
                    {formikFieldMeta.touched && formikFieldMeta.error && (
                        <FormHelperText className={localClasses.error}>
                            {formikFieldMeta.error}
                        </FormHelperText>
                    )}
                </FormControl>
            )}
        </>
    );
};
